import { SPORTS } from '../../data/constants';
import { legacySportKey } from '../../data/sports';
import { StatCards, SectionCard, HBar, MiniLineChart, EmptyState, last8Weeks, parseDateStr } from './shared';

const CARDIO_SPORTS = ['zone2', 'hiit', 'outdoor_run', 'indoor_cycle', 'road_cycle', 'stairmill'];

export default function CardioAnalysis({ records }) {
    if (!records.length) return <EmptyState text="유산소 기록이 없어요." />;

    const totalMin = records.reduce((a, r) => a + (+r.duration || 0), 0);
    const totalDist = records.reduce((a, r) => a + (+r.distance || 0), 0);
    const totalCal = records.reduce((a, r) => a + (+r.calories || 0), 0);
    const hrRecs = records.filter(r => +r.avgHR > 0);
    const avgHR = hrRecs.length ? Math.round(hrRecs.reduce((a, r) => a + (+r.avgHR), 0) / hrRecs.length) : null;

    // 종목별 분포
    const sportCounts = {};
    records.forEach(r => {
        const k = legacySportKey(r);
        sportCounts[k] = (sportCounts[k] || 0) + 1;
    });
    const maxSport = Math.max(...Object.values(sportCounts), 1);

    // 주간 거리 (km)
    const weeklyDist = last8Weeks(records, recs => +recs.reduce((a, r) => a + (+r.distance || 0), 0).toFixed(1));

    // 주간 칼로리
    const weeklyCal = last8Weeks(records, recs => recs.reduce((a, r) => a + (+r.calories || 0), 0));

    // 심박수 추이 (최근 12회 기록)
    const hrTrend = records
        .filter(r => +r.avgHR > 0)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(-12)
        .map(r => {
            const d = parseDateStr(r.date);
            return { label: `${d.getMonth() + 1}/${d.getDate()}`, val: +r.avgHR };
        });

    // 스텝밀 전용: 층수 추이
    const stairmillRecs = records.filter(r => legacySportKey(r) === 'stairmill' && +r.floors > 0)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(-10)
        .map(r => {
            const d = parseDateStr(r.date);
            return { label: `${d.getMonth() + 1}/${d.getDate()}`, val: +r.floors };
        });

    // 러닝 페이스 추이
    const runRecs = records.filter(r => legacySportKey(r) === 'outdoor_run' && r.pace)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(-10);
    const paceTrend = runRecs.map(r => {
        const d = parseDateStr(r.date);
        const [min, sec] = (r.pace || '0:0').split(':').map(Number);
        return { label: `${d.getMonth() + 1}/${d.getDate()}`, val: +(min + sec / 60).toFixed(2) };
    });

    const hasDistance = records.some(r => +r.distance > 0);
    const hasCal = records.some(r => +r.calories > 0);

    return (
        <>
            <StatCards items={[
                { val: `${records.length}회`, label: '총 세션' },
                { val: `${Math.round(totalMin / 60)}h`, label: '총 시간' },
                ...(hasDistance ? [{ val: `${totalDist.toFixed(1)}km`, label: '총 거리' }] : []),
                ...(hasCal ? [{ val: `${Math.round(totalCal / 1000)}kcal`, label: '총 칼로리' }] : []),
                ...(avgHR ? [{ val: `${avgHR}bpm`, label: '평균 심박수' }] : []),
            ]} />

            <SectionCard title="종목 분포" desc="유산소 종목별 세션 수">
                {CARDIO_SPORTS.filter(k => sportCounts[k]).map(k => (
                    <HBar key={k} label={SPORTS[k]?.name || k} value={sportCounts[k]} max={maxSport} color={SPORTS[k]?.color || 'var(--accent)'} suffix="회" />
                ))}
            </SectionCard>

            {hasDistance && (
                <SectionCard title="주간 거리" desc="주별 누적 거리 (km)">
                    <div className="chart-wrap">
                        <div className="chart-title">
                            <span>주간 거리</span>
                            <span style={{ fontFamily: 'DM Mono,monospace', color: 'var(--accent)' }}>
                                {weeklyDist[weeklyDist.length - 1]?.val}km
                            </span>
                        </div>
                        <div className="bar-chart" style={{ alignItems: 'flex-end', gap: 4 }}>
                            {weeklyDist.map((w, i) => {
                                const isLatest = i === weeklyDist.length - 1;
                                const maxV = Math.max(...weeklyDist.map(x => x.val), 1);
                                const h = Math.round((w.val / maxV) * 72) + 4;
                                return (
                                    <div key={i} className="chart-bar-wrap">
                                        {w.val > 0 && (
                                            <span style={{ fontSize: 9, fontFamily: 'DM Mono,monospace', color: isLatest ? 'var(--accent)' : 'var(--text2)', marginBottom: 3 }}>
                                                {w.val}
                                            </span>
                                        )}
                                        <div className="chart-bar" style={{ height: h, background: w.val ? (isLatest ? 'var(--accent)' : 'var(--bg4)') : 'var(--bg3)' }} />
                                        <div className="chart-label" style={{ color: isLatest ? 'var(--text2)' : 'var(--text3)' }}>{w.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </SectionCard>
            )}

            {hrTrend.length >= 2 && (
                <SectionCard title="심박수 추이" desc="최근 기록별 평균 심박수 변화">
                    <MiniLineChart data={hrTrend} color="#ff7c5c" label="평균 심박수 (bpm)" />
                </SectionCard>
            )}

            {paceTrend.length >= 2 && (
                <SectionCard title="러닝 페이스 추이" desc="실외 러닝 페이스 변화 (낮을수록 빠름)">
                    <MiniLineChart data={paceTrend} color="#5ba4f5" label="페이스 (분/km)" />
                </SectionCard>
            )}

            {stairmillRecs.length >= 2 && (
                <SectionCard title="스텝밀 층수 추이" desc="세션별 총 층수 변화">
                    <MiniLineChart data={stairmillRecs} color="#ffa44f" label="총 층수" />
                </SectionCard>
            )}

            {hasCal && (
                <SectionCard title="주간 칼로리" desc="주별 누적 소모 칼로리">
                    <div className="chart-wrap">
                        <div className="chart-title">
                            <span>주간 칼로리</span>
                            <span style={{ fontFamily: 'DM Mono,monospace', color: '#ff7c5c' }}>
                                {weeklyCal[weeklyCal.length - 1]?.val}kcal
                            </span>
                        </div>
                        <div className="bar-chart" style={{ alignItems: 'flex-end', gap: 4 }}>
                            {weeklyCal.map((w, i) => {
                                const isLatest = i === weeklyCal.length - 1;
                                const maxV = Math.max(...weeklyCal.map(x => x.val), 1);
                                const h = Math.round((w.val / maxV) * 72) + 4;
                                return (
                                    <div key={i} className="chart-bar-wrap">
                                        {w.val > 0 && (
                                            <span style={{ fontSize: 9, fontFamily: 'DM Mono,monospace', color: isLatest ? '#ff7c5c' : 'var(--text2)', marginBottom: 3 }}>
                                                {w.val}
                                            </span>
                                        )}
                                        <div className="chart-bar" style={{ height: h, background: w.val ? (isLatest ? '#ff7c5c' : 'var(--bg4)') : 'var(--bg3)' }} />
                                        <div className="chart-label" style={{ color: isLatest ? 'var(--text2)' : 'var(--text3)' }}>{w.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </SectionCard>
            )}
        </>
    );
}
